import React, { type JSX } from 'react';
import { observer } from 'mobx-react';
import { observable, action, runInAction, makeObservable } from 'mobx';

import { type NoteInfo } from '@root/models/NoteInfo';
import { type CategoryInfo } from '@root/models/CategoryInfo';
import { PromiseCompletion } from '@classes/PromiseCompletion';
import { apiService } from '@services/ApiService';
import { notificationService } from '@services/NotificationService';

import CategoryPicker from '@components/CategoryPicker';
import { ImgShield } from '@components/Images/ImgShield';
import { type IModalDialogContent, type ModalButtonOptions, ModalButtonType, type ModalDialogOptions, ModalWindow } from '@components/Modal';

import './_note-details-dialog.scss';

export type NoteDetailsDialogProps = {
  note: NoteInfo;
  // Fired after a successful save. `categoryChanged` lets the page refresh
  // category counts only when actually needed (not on name/color/pin edits).
  onSaved?: (note: NoteInfo, categoryChanged: boolean) => void;
};

/** Curated accent palette for quick color picks (plus a "custom" picker). */
const COLOR_PALETTE = [
  '#e11d48', '#f97316', '#f59e0b', '#eab308',
  '#84cc16', '#10b981', '#06b6d4', '#3b82f6',
  '#6366f1', '#a855f7', '#ec4899', '#94a3b8'
];

const NO_COLOR = '';

@observer
export default class NoteDetailsDialog extends React.Component<NoteDetailsDialogProps> implements IModalDialogContent<void> {
  @observable private _name: string;
  @observable private _color: string;
  @observable private _isPinned: boolean;
  @observable private _isProtected: boolean;
  @observable private _activeCategoryId: string;
  @observable private _categories: CategoryInfo[] = [];
  @observable private _newCategoryName = 'New category';
  @observable private _isCustomColor = false;

  private _loader: PromiseCompletion = new PromiseCompletion();
  private _nameRef: React.RefObject<HTMLInputElement> = React.createRef();

  constructor(props: NoteDetailsDialogProps) {
    super(props);
    makeObservable(this);
    const { note } = props;
    this._name = note.name || '';
    this._color = note.color || NO_COLOR;
    this._isPinned = !!note.isPinned;
    this._isProtected = !!note.isProtected;
    // Uncategorized notes start on the virtual Default bucket.
    this._activeCategoryId = note.categoryId || 'default';
  }

  public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
    const buttons: ModalButtonOptions<void>[] = [
      {
        type: ModalButtonType.Cancel,
        onClick: () => window.close()
      },
      {
        title: 'Save',
        type: ModalButtonType.Save,
        isDisabled: this._loader.isPending,
        onClick: () => this._onSaveClick(window)
      }
    ];

    return {
      title: (
        <span className="ndd-title">
          <span
            className="ndd-title-dot"
            style={this._color ? { background: this._color } : undefined}
          />
          <span className="ndd-title-name">{this._name || 'Untitled note'}</span>
        </span>
      ),
      width: 520,
      buttons,
      loader: this._loader
    };
  }

  componentDidMount(): void {
    this._nameRef.current?.focus();
    this._nameRef.current?.select();
    void this._loadCategories();
  }

  render(): JSX.Element {
    const colorActive = this._color && !this._isCustomColor;

    return (
      <div className="note-details-dialog">
        {/* Name */}
        <div className="ndd-field">
          <label className="ndd-label">Name</label>
          <input
            ref={this._nameRef}
            className="ndd-input ndd-input-name"
            type="text"
            value={this._name}
            placeholder="Note title"
            onChange={action((e) => { this._name = e.target.value; })}
          />
        </div>

        {/* Color */}
        <div className="ndd-field">
          <label className="ndd-label">Color</label>
          <div className="ndd-colors">
            {/* Empty: transparent checkerboard disc */}
            <button
              type="button"
              className={`ndd-swatch ndd-swatch-empty${this._color === NO_COLOR ? ' active' : ''}`}
              title="No color"
              aria-label="No color"
              onClick={action(() => this._setColor(NO_COLOR))}
            />
            {COLOR_PALETTE.map(hex => (
              <button
                key={hex}
                type="button"
                className={`ndd-swatch${this._color === hex ? ' active' : ''}`}
                style={{ background: hex }}
                title={hex}
                aria-label={`Color ${hex}`}
                onClick={action(() => this._setColor(hex))}
              />
            ))}
            {/* Custom: rainbow disc that opens the native picker */}
            <label
              className={`ndd-swatch ndd-swatch-custom${this._isCustomColor ? ' active' : ''}`}
              title="Pick a custom color"
            >
              <input
                type="color"
                value={this._color && this._color !== NO_COLOR ? this._color : '#3b82f6'}
                onChange={action((e) => this._setColor(e.target.value, true))}
              />
            </label>
          </div>
          {colorActive && (
            <div className="ndd-color-clear">
              <button type="button" className="ndd-link" onClick={action(() => this._setColor(NO_COLOR))}>
                Clear color
              </button>
            </div>
          )}
        </div>

        {/* Category */}
        <div className="ndd-field">
          <label className="ndd-label">Category</label>
          <CategoryPicker
            categories={this._categories}
            activeCategoryId={this._activeCategoryId}
            onCategoryChanged={action((c: CategoryInfo) => { this._activeCategoryId = c.id; })}
          />
          {this._activeCategoryId === '' && (
            <input
              className="ndd-input"
              type="text"
              placeholder="New category name"
              value={this._newCategoryName}
              onChange={action((e) => { this._newCategoryName = e.target.value; })}
            />
          )}
        </div>

        {/* Toggles */}
        <div className="ndd-toggles">
          <button
            type="button"
            className={`ndd-toggle${this._isPinned ? ' on' : ''}`}
            onClick={action(() => { this._isPinned = !this._isPinned; })}
          >
            <span className="ndd-toggle-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15" fill={this._isPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 17v5M9 3h6l-1 7 3 3H7l3-3-1-7z" /></svg>
            </span>
            <span className="ndd-toggle-text">
              <span className="ndd-toggle-title">Pin to top</span>
              <span className="ndd-toggle-sub">{this._isPinned ? 'Pinned' : 'Not pinned'}</span>
            </span>
            <span className="ndd-toggle-switch" aria-hidden="true"><span className="ndd-toggle-knob" /></span>
          </button>

          <button
            type="button"
            className={`ndd-toggle protect${this._isProtected ? ' on' : ''}`}
            onClick={action(() => { this._isProtected = !this._isProtected; })}
          >
            <span className="ndd-toggle-icon" aria-hidden="true">
              <ImgShield size={15} />
            </span>
            <span className="ndd-toggle-text">
              <span className="ndd-toggle-title">Protect</span>
              <span className="ndd-toggle-sub">{this._isProtected ? 'Content is blurred' : 'Blur content in the list'}</span>
            </span>
            <span className="ndd-toggle-switch" aria-hidden="true"><span className="ndd-toggle-knob" /></span>
          </button>
        </div>
      </div>
    );
  }

  @action
  private _setColor(color: string, custom = false): void {
    this._color = color;
    this._isCustomColor = custom && color !== NO_COLOR;
  }

  private async _loadCategories(): Promise<void> {
    const data = await apiService.get<CategoryInfo[]>('/api/note/category', null, {
      loader: this._loader
    });

    // Legacy uncategorized notes can be moved back to the virtual Default bucket.
    if (!this.props.note.categoryId) {
      data.push({ id: 'default', name: 'Default', sortOrder: 0 });
    }
    data.push({ id: '', name: 'New category', sortOrder: Number.MAX_SAFE_INTEGER });

    runInAction(() => { this._categories = data; });
  }

  @action.bound
  private async _onSaveClick(window: ModalWindow<void>): Promise<void> {
    const { note } = this.props;
    const id = note.id;

    const name = this._name.trim();
    const nameChanged = name !== (note.name || '');
    const colorChanged = (this._color || NO_COLOR) !== (note.color || NO_COLOR);
    const categoryChanged = (this._activeCategoryId === 'default' ? '' : this._activeCategoryId) !== (note.categoryId || '');
    const pinChanged = this._isPinned !== !!note.isPinned;
    const protectChanged = this._isProtected !== !!note.isProtected;

    if (!name) {
      notificationService.error('Name cannot be empty');
      return;
    }

    // Fire the independent updates in parallel.
    const tasks: Promise<unknown>[] = [];
    if (nameChanged) tasks.push(apiService.put('/api/note/' + id + '/name', { name }));
    if (colorChanged) tasks.push(apiService.put('/api/note/' + id + '/color', { color: this._color }));
    if (protectChanged) tasks.push(apiService.post('/api/note/' + id + '/toggle-protect'));
    if (pinChanged) tasks.push(apiService.post('/api/note/' + id + '/toggle-pin'));
    if (categoryChanged) {
      tasks.push(this._saveCategory(id, this._activeCategoryId, this._newCategoryName));
    }

    await Promise.all(tasks);

    // Reflect the new state on the shared note object (drives the list UI).
    runInAction(() => {
      note.name = name;
      note.color = this._color;
      note.isPinned = this._isPinned;
      note.isProtected = this._isProtected;
      if (categoryChanged) {
        note.categoryId = this._activeCategoryId === 'default' ? '' : this._activeCategoryId;
      }
    });

    this.props.onSaved?.(note, categoryChanged);
    window.close(ModalButtonType.Save);
  }

  /** Assigns the note to a category, creating one first when the id is ''. */
  private async _saveCategory(noteId: string, categoryId: string, newName: string): Promise<void> {
    let targetId = categoryId;
    if (targetId === '') {
      const created = await apiService.post<CategoryInfo>('/api/note/category', {
        name: newName.trim() || 'New category'
      });
      targetId = created.id;
    }
    // 'default' maps to an empty categoryId (legacy uncategorized bucket).
    const payload = targetId === 'default' ? '' : targetId;
    await apiService.put(`/api/note/${noteId}/category`, { noteId, categoryId: payload });
  }
}
